import "./BulkBgRemover.css";
import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  fetchCreationImageBlob,
  fetchUploadImageBlob,
  fetchExportImageBlob,
  removeBackgroundSingle,
} from "../../services/api/editor/background_removal";
import ImagePickerModal from "../../components/popups/imagePicker/ImagePickerModal";
import { useImagePickerModal } from "../../hooks/app/useImagePickerModal";
import {
  libraryItemCreationId,
  libraryItemExportId,
} from "../../utils/openLibraryInEditor";

const BulkBgRemover = () => {
  const { getToken } = useAuth();
  const { openImagePicker, imagePickerProps } = useImagePickerModal();
  const [images, setImages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkError, setBulkError] = useState(null);

  const uploadUrl = process.env.REACT_APP_UPLOAD_URL || null;

  const addImageFile = (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    setImages((prev) => [
      {
        id: `${Date.now()}-0`,
        file,
        url: URL.createObjectURL(file),
        uploadProgress: 0,
        processProgress: 0,
        selected: false,
        uploading: true,
        processing: false,
        processed: false,
      },
      ...prev,
    ]);
  };

  const openFilePicker = () => {
    openImagePicker({
      onPickFile: addImageFile,
      onPickLibrary: async (item) => {
        try {
          const token = await getToken();
          if (!token) return;
          let blob;
          if (item.kind === "upload") {
            blob = await fetchUploadImageBlob(token, libraryItemCreationId(item));
          } else if (item.kind === "export") {
            blob = await fetchExportImageBlob(token, libraryItemExportId(item));
          } else {
            blob = await fetchCreationImageBlob(token, libraryItemCreationId(item));
          }
          const name = item.file_name || item.label || "image.png";
          const file = new File([blob], name, { type: blob.type || "image/png" });
          addImageFile(file);
        } catch (err) {
          console.error(err);
          setBulkError("Could not load that library image.");
        }
      },
    });
  };

  // remove image
  const removeImage = (id) => {
    setImages((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found && found.url) URL.revokeObjectURL(found.url);
      return prev.filter((it) => it.id !== id);
    });
  };

  // toggle select
  const toggleSelect = (id) => {
    setImages((prev) =>
      prev.map((it) => (it.id === id ? { ...it, selected: !it.selected } : it))
    );
  };

  // simulate upload progress or perform real upload if REACT_APP_UPLOAD_URL is set
  useEffect(() => {
    const timers = [];

    images.forEach((img) => {
      if (img.uploading && img.uploadProgress < 100) {
        if (uploadUrl) {
          // example real upload flow using XHR to capture progress
          const xhr = new XMLHttpRequest();
          const fd = new FormData();
          fd.append("file", img.file);
          xhr.open("POST", uploadUrl);
          xhr.upload.onprogress = (ev) => {
            const pct = ev.lengthComputable
              ? Math.round((ev.loaded / ev.total) * 100)
              : img.uploadProgress;
            setImages((prev) =>
              prev.map((p) =>
                p.id === img.id
                  ? { ...p, uploadProgress: pct, uploading: pct < 100 }
                  : p
              )
            );
          };
          xhr.onload = () =>
            setImages((prev) =>
              prev.map((p) =>
                p.id === img.id
                  ? { ...p, uploadProgress: 100, uploading: false }
                  : p
              )
            );
          xhr.onerror = () =>
            setImages((prev) =>
              prev.map((p) =>
                p.id === img.id ? { ...p, uploading: false } : p
              )
            );
          try {
            xhr.send(fd);
          } catch (err) {
            console.warn("Upload failed", err);
            setImages((prev) =>
              prev.map((p) =>
                p.id === img.id ? { ...p, uploading: false } : p
              )
            );
          }
        } else {
          const t = setInterval(() => {
            setImages((prev) =>
              prev.map((p) => {
                if (p.id !== img.id) return p;
                const next = Math.min(
                  100,
                  p.uploadProgress + Math.floor(Math.random() * 15) + 5
                );
                return { ...p, uploadProgress: next, uploading: next < 100 };
              })
            );
          }, 500 + Math.random() * 700);
          timers.push(t);
        }
      }
    });

    return () => timers.forEach((t) => clearInterval(t));
  }, [images, uploadUrl]);

  // revoke object URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    };
  }, []);

  // select all behavior
  useEffect(() => {
    setImages((prev) => prev.map((p) => ({ ...p, selected: selectAll })));
  }, [selectAll]);

  // bulk processing via bgremove (sequential single removes)
  const processSelected = async () => {
    const selected = images.filter((p) => p.selected && p.uploadProgress === 100);
    if (selected.length === 0) return;

    setBulkError(null);
    setImages((prev) =>
      prev.map((p) =>
        p.selected && p.uploadProgress === 100
          ? { ...p, processing: true, processProgress: 0 }
          : p
      )
    );

    try {
      const token = await getToken();
      if (!token) {
        setBulkError("Please sign in to use bulk removal.");
        setImages((prev) =>
          prev.map((p) => (p.processing ? { ...p, processing: false } : p))
        );
        return;
      }

      const total = selected.length;
      for (let i = 0; i < selected.length; i++) {
        const item = selected[i];
        const response = await removeBackgroundSingle(token, item.file);
        if (!response?.output_url && !response?.creation_id) {
          throw new Error(response?.message || "Background removal failed.");
        }

        let newUrl = response.output_url;
        if (response.creation_id) {
          try {
            const blob = await fetchCreationImageBlob(token, response.creation_id);
            newUrl = URL.createObjectURL(blob);
          } catch (_) {
            // fall back to output_url from API
          }
        }

        const pct = Math.round(((i + 1) / total) * 100);
        setImages((prev) =>
          prev.map((p) => {
            if (p.id !== item.id) return p;
            if (p.url && p.url !== newUrl && p.url.startsWith("blob:")) {
              URL.revokeObjectURL(p.url);
            }
            return {
              ...p,
              url: newUrl || p.url,
              processProgress: pct,
              processing: pct < 100,
              processed: pct >= 100,
            };
          })
        );
      }
    } catch (err) {
      setBulkError(err.message || "Bulk removal failed.");
      setImages((prev) =>
        prev.map((p) => (p.processing ? { ...p, processing: false } : p))
      );
    }
  };

  return (
    <div className="bulk-bg-remover-container">
      <ImagePickerModal {...imagePickerProps} />
      <h1>Bulk Background Removal</h1>
      <div className="feature-box">
        <div className="feature-description">
          <p>Remove backgrounds from hundreds of images in seconds.</p>
          <p>Upload, process and download — all in one click.</p>
          <p>Perfect for e-commerce, studios and creators.</p>
        </div>
        <div>
          <button id="upload-img-btn" onClick={openFilePicker}>
            Upload Images
          </button>
        </div>
      </div>

      {bulkError && (
        <div className="bulk-error" role="alert">
          {bulkError}
        </div>
      )}
      {images.length > 0 && (
        <div className="thumbnail-grid">
          <div className="toolbar">
            <label className="toolbar-select">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(e) => setSelectAll(e.target.checked)}
              />{" "}
              Select all
            </label>
            <button
              className="bulk-btn"
              onClick={processSelected}
              aria-label="Remove Bulk Background"
            >
              Remove Bulk Background
            </button>
          </div>

          {images.map((img) => (
            <div className="thumb-card" key={img.id}>
              <div className="thumb-image-wrapper">
                <img
                  src={img.url}
                  alt={img.file.name || "uploaded image"}
                  className={`thumb-image ${
                    img.uploadProgress < 100 && !img.processed ? "blurred" : ""
                  }`}
                />

                {/* (Removed checkerboard overlay and split divider - show full image only) */}

                {/* actions */}
                <button
                  className="delete-btn"
                  onClick={() => removeImage(img.id)}
                  title="Delete"
                >
                  🗑
                </button>

                <label className="select-box">
                  <input
                    type="checkbox"
                    checked={img.selected}
                    onChange={() => toggleSelect(img.id)}
                  />
                </label>

                {/* progress overlay: if processing show processProgress else uploadProgress */}
                <div className="progress-overlay">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${
                          img.processing || img.processed
                            ? img.processProgress
                            : img.uploadProgress
                        }%`,
                      }}
                    />
                  </div>
                  <div className="progress-text">
                    {img.processing || img.processed
                      ? `${img.processProgress} % Processed`
                      : `${img.uploadProgress} % Uploaded`}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkBgRemover;
